import { Box, Checkbox, FormControlLabel, type SxProps, type Theme, Typography } from "@mui/material";
import type { ChangeEvent, FC, InputHTMLAttributes, ReactNode } from "react";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { RoundedCheckboxBox, RoundedCheckboxState } from "./RoundedCheckboxBox";

type Props = {
	checked: boolean;
	onChange: (checked: boolean) => void;
	label: string;
	disabled?: boolean;
	/**
	 * Tri-state: renders the box in the mixed/dash state (a parent "select-all" whose children are
	 * partially selected). Additive + optional; existing callers keep their plain checked/unchecked box.
	 */
	indeterminate?: boolean;
	/** Optional node rendered between the box and the label (e.g. a categorical color dot). */
	startAdornment?: ReactNode;
	/** Optional right-aligned muted slot (e.g. a facet count). Pair with `fullWidth` so it anchors to the row end. */
	secondary?: ReactNode;
	/** Stretch the row to the container width, pushing `secondary` to the far end; label truncates with ellipsis. */
	fullWidth?: boolean;
	/**
	 * Hover explanation for the row — most useful for saying WHY the box is disabled.
	 *
	 * A disabled MUI control receives no pointer events, so a bare Tooltip around it never opens; the
	 * span wrapper below is what makes it hoverable. Encoded here rather than at each call site so a
	 * disabled checkbox can always explain itself in one line.
	 */
	tooltip?: string;
	"data-testid"?: string;
	/**
	 * Marks the box as the control a walkthrough's Next presses (`data-walkthrough-reveal`). Lands on the
	 * native input, so the tour's `.click()` toggles it exactly like a user's click and a disabled box
	 * (which gets the `disabled` attribute) is correctly passed over. Additive; unset for every other caller.
	 */
	"data-walkthrough-reveal"?: string;
	sx?: SxProps<Theme>;
};

/**
 * A checkbox with fully rounded (pill-corner) box, plus a label.
 * Controlled: pass `checked` and handle `onChange(nextChecked)`.
 * Supports an optional leading adornment (color dot) and a right-aligned
 * secondary slot (count) for faceted-filter rows.
 */
export const SystemRoundedCheckbox: FC<Props> = ({
	checked,
	onChange,
	label,
	disabled,
	indeterminate,
	startAdornment,
	secondary,
	fullWidth,
	tooltip,
	"data-testid": dataTestId,
	"data-walkthrough-reveal": walkthroughReveal,
	sx,
}) => {
	// A named const, not an inline literal: TypeScript rejects a `data-*` key on a fresh object literal typed
	// as the slot's props, while the DOM takes it as-is.
	const walkthroughInputProps: InputHTMLAttributes<HTMLInputElement> & { "data-walkthrough-reveal"?: string } = {
		"data-walkthrough-reveal": walkthroughReveal,
	};
	const row = (
		<FormControlLabel
			data-testid={dataTestId}
			disabled={disabled}
			sx={{
				marginLeft: "0 !important",
				marginRight: "0 !important",
				...(fullWidth ? { width: "100%", "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 } } : {}),
				...sx,
			}}
			control={
				<Checkbox
					checked={checked}
					indeterminate={indeterminate}
					disabled={disabled}
					disableRipple
					slotProps={{ input: walkthroughInputProps }}
					onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
					sx={{ p: 0.5 }}
					icon={<RoundedCheckboxBox state={RoundedCheckboxState.Off} disabled={disabled} />}
					indeterminateIcon={<RoundedCheckboxBox state={RoundedCheckboxState.Indeterminate} disabled={disabled} />}
					checkedIcon={<RoundedCheckboxBox state={RoundedCheckboxState.On} disabled={disabled} />}
				/>
			}
			label={
				<Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.75, pl: 0.75 }}>
					{startAdornment}
					<Typography
						title={fullWidth ? label : undefined}
						sx={{
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: "100%",
							color: disabled ? INK.DISABLED : INK.SECONDARY,
							whiteSpace: "nowrap",
							...(fullWidth ? { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" } : {}),
						}}
					>
						{label}
					</Typography>
					{secondary !== undefined && (
						<Typography
							component="span"
							sx={{
								fontSize: FONT_SIZE.SECONDARY,
								fontWeight: 500,
								fontVariantNumeric: "tabular-nums",
								color: disabled ? INK.DISABLED : INK.MUTED,
								whiteSpace: "nowrap",
								marginInlineStart: "auto",
							}}
						>
							{secondary}
						</Typography>
					)}
				</Box>
			}
		/>
	);

	if (!tooltip) return row;
	// The span is load-bearing: a disabled control swallows pointer events, so without it the tooltip
	// never opens on exactly the rows that most need to explain themselves.
	return (
		<InHouseTooltip title={tooltip}>
			<Box component="span" sx={{ display: "inline-flex" }}>
				{row}
			</Box>
		</InHouseTooltip>
	);
};
